import React, { useState, useEffect, forwardRef, useCallback } from 'react'
import { Grid, Card, Radio, Text, Flex, Stack, Box, Label } from '@sanity/ui'
import { set, unset } from 'sanity'
import { useClient } from 'sanity'
import { ObjectInputProps } from 'sanity'

interface Diameter {
  _id: string
  name: string
  wheelDiameter: number
}

const WheelDiameterSelector = forwardRef<HTMLDivElement, ObjectInputProps>(
  (props, ref) => {
    const { value, readOnly, onChange, schemaType } = props

    const [diameters, setDiameters] = useState<Diameter[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const client = useClient({ apiVersion: '2023-05-03' })

    useEffect(() => {
      let mounted = true

      const fetchDiameters = async () => {
        try {
          setIsLoading(true)
          const query = `*[
            _type == "diameter"
            && defined(wheelDiameter)
            && wheelDiameter != null
          ] | order(wheelDiameter asc) {
            _id,
            name,
            wheelDiameter
          }`
          const result = await client.fetch<Diameter[]>(query)
          if (mounted) {
            // Filter out legacy wheel diameters; the ones that used string in a name
            const filteredDiameters = result.filter(
              (diameter) => String(diameter.name).length <= 3,
            )
            setDiameters(filteredDiameters)
          }
        } catch (err) {
          if (mounted) {
            setError('Failed to load wheel diameters')
            console.error('Error fetching wheel diameters:', err)
          }
        } finally {
          if (mounted) {
            setIsLoading(false)
          }
        }
      }

      fetchDiameters()

      return () => {
        mounted = false
      }
    }, [client])

    const handleChange = useCallback(
      (selectedId: string) => {
        onChange(
          selectedId
            ? set({
                _type: 'reference',
                _ref: selectedId,
              })
            : unset(),
        )
      },
      [onChange],
    )

    return (
      <Stack gap={3} ref={ref}>
        <Box>
          <Label size={1}>{schemaType.title}</Label>
          {schemaType.description && (
            <Box paddingTop={2}>
              <Label size={0} muted>
                {schemaType.description}
              </Label>
            </Box>
          )}
        </Box>

        <Stack gap={3} paddingTop={2}>
          {error && (
            <Text size={1} style={{ color: 'red' }}>
              {error}
            </Text>
          )}

          {isLoading ? (
            <Text size={1}>Loading wheel diameters...</Text>
          ) : (
            <Grid gridTemplateColumns={[1, 2, 4]} gap={3}>
              {diameters.map((diameter) => (
                <Card
                  key={diameter._id}
                  padding={3}
                  radius={2}
                  shadow={1}
                  tone={value?._ref === diameter._id ? 'primary' : 'default'}
                  onClick={() => !readOnly && handleChange(diameter._id)}
                  style={{
                    cursor: readOnly ? 'default' : 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <Flex align="center" gap={2}>
                    <Radio
                      checked={value?._ref === diameter._id}
                      readOnly
                      disabled={readOnly}
                    />
                    <Text
                      size={2}
                      weight={value?._ref === diameter._id ? 'bold' : 'regular'}
                    >
                      {diameter.wheelDiameter}"
                    </Text>
                  </Flex>
                </Card>
              ))}
            </Grid>
          )}
        </Stack>
      </Stack>
    )
  },
)

WheelDiameterSelector.displayName = 'WheelDiameterSelector'

export default WheelDiameterSelector
